import { useState } from "preact/hooks";
import { authPost } from "../../api/client.ts";
import type { AuthStep, SignUp as SignUpBody } from "../../api/types.gen.ts";
import { SLOT, t } from "../../i18n/index.ts";
import { location } from "../../lib/router.ts";
import { Button } from "../../ui/Button.tsx";
import { Field, Form, Input } from "../../ui/form.tsx";
import { Slotted } from "../../ui/misc.tsx";
import { authPath, fieldError, MIN_PASSWORD, next, useSubmit } from "./flow.ts";
import { AuthFrame, AuthLink, FormError } from "./frame.tsx";
import { TermsCheckbox } from "./terms.tsx";

/**
 * Creating an account: a name (one field, as they write it), email and password, accepting the Terms of service and
 * Privacy policy (recorded with the account); then the emailed code.
 */
export function SignUp() {
  const [name, setName] = useState("");
  // From an invitation's link: the address it went to.
  const [email, setEmail] = useState(location.value.searchParams.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const create = useSubmit(async () =>
    next(await authPost<AuthStep>("/sign-up", { name, email, password, terms } satisfies SignUpBody)),
  );
  return (
    <AuthFrame
      title={t("auth.signUp.title")}
      lede={t("auth.signUp.lede")}
      footer={
        <Slotted text={t("auth.signUp.have", { link: SLOT })}>
          <AuthLink href={authPath("/signin")}>{t("auth.signIn.submit")}</AuthLink>
        </Slotted>
      }
    >
      <FormError error={create.error} email={email} />
      <Form class="flex flex-col gap-4" onSubmit={create.submit}>
        <Field label={t("auth.name")} hint={t("auth.nameHint")}>
          <Input autocomplete="name" required={true} maxLength={200} value={name} onValue={setName} />
        </Field>
        <Field label={t("auth.email")} error={fieldError(create.error, "email")}>
          <Input type="email" autocomplete="email" required={true} value={email} onValue={setEmail} />
        </Field>
        <Field
          label={t("auth.password")}
          hint={t("auth.passwordHint", { min: MIN_PASSWORD })}
          error={fieldError(create.error, "password")}
        >
          <Input
            type="password"
            autocomplete="new-password"
            required={true}
            minLength={MIN_PASSWORD}
            value={password}
            onValue={setPassword}
          />
        </Field>
        <TermsCheckbox checked={terms} onCheck={setTerms} />
        <Button variant="primary" submit={true} busy={create.busy} class="mt-2 w-full">
          {t("auth.signUp.submit")}
        </Button>
      </Form>
    </AuthFrame>
  );
}
